import React, { useState, useRef, useEffect, useContext } from 'react';
import { FaBars } from 'react-icons/fa';
import { FiSearch, FiMail, FiBell, FiClock, FiUser, FiLogOut, FiShoppingCart, FiMessageSquare } from 'react-icons/fi';
import { IoMdArrowDropdown } from 'react-icons/io';
import { useNavigate } from 'react-router-dom';
import notificationService from '../services/notificationService';
import messageService from '../services/messageService';
import eventBus from '../utils/eventBus';
import CartContext from '../context/CartContext';

const Header = ({ user, toggleSidebar, isSidebar = false, logout, cartItems: propCartItems, setServiceMode, serviceMode }) => {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const [notifications, setNotifications] = useState([]);
  const [notificationsLoading, setNotificationsLoading] = useState(false);

  // Get cart from context if available
  const cartContext = useContext(CartContext);
  const hasCartContext = cartContext !== undefined;

  // Use either the cart from context or from props
  const cartItems = hasCartContext ? cartContext.cart : propCartItems;

  // Track cart item count
  const [cartItemCount, setCartItemCount] = useState(cartItems ? Object.keys(cartItems || {}).length : 0);

  const dropdownRef = useRef(null);
  const notificationsRef = useRef(null);
  const navigate = useNavigate();

  // Update cart item count whenever cartItems prop or context changes
  useEffect(() => {
    setCartItemCount(cartItems ? Object.keys(cartItems || {}).length : 0);
  }, [cartItems]);

  // Subscribe to cart:updated events from eventBus
  useEffect(() => {
    // Set initial count
    setCartItemCount(cartItems ? Object.keys(cartItems || {}).length : 0);

    // Subscribe to cart updates
    const unsubscribe = eventBus.on('cart:updated', (updatedCart) => {
      if (updatedCart) {
        setCartItemCount(Object.keys(updatedCart).length);
      }
    });

    // Cleanup
    return () => {
      unsubscribe();
    };
  }, []);

  // Fetch notifications and counts
  useEffect(() => {
    if (user && user.id) {
      fetchNotificationCounts();

      // Listen for real-time updates
      window.addEventListener('notification-count-updated', handleNotificationCountUpdate);
      window.addEventListener('message-count-updated', handleMessageCountUpdate);

      return () => {
        window.removeEventListener('notification-count-updated', handleNotificationCountUpdate);
        window.removeEventListener('message-count-updated', handleMessageCountUpdate);
      };
    }
  }, [user]);

  // Handle clicks outside dropdowns
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownOpen(false);
      }
      if (notificationsRef.current && !notificationsRef.current.contains(event.target)) {
        setNotificationsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Fetch notification counts
  const fetchNotificationCounts = async () => {
    try {
      // Fetch unread notification count
      const notifResponse = await notificationService.getUnreadCount();
      setUnreadNotifications(notifResponse.count || 0);

      // Fetch unread message count
      const msgResponse = await messageService.getUnreadCount();
      setUnreadMessages(msgResponse.count || 0);
    } catch (error) {
      console.error('Error fetching notification counts:', error);
    }
  };

  // Fetch recent notifications when dropdown opens
  const fetchRecentNotifications = async () => {
    if (!notificationsOpen) return;

    try {
      setNotificationsLoading(true);
      const response = await notificationService.getRecentNotifications(5);
      setNotifications(response.notifications || []);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setNotificationsLoading(false);
    }
  };

  // Handle real-time notification count updates
  const handleNotificationCountUpdate = (event) => {
    setUnreadNotifications(event.detail.count);
  };

  // Handle real-time message count updates
  const handleMessageCountUpdate = (event) => {
    setUnreadMessages(event.detail.count);
  };

  // Toggle dropdowns
  const toggleDropdown = () => setDropdownOpen(!dropdownOpen);

  const toggleNotifications = () => {
    const newState = !notificationsOpen;
    setNotificationsOpen(newState);

    // Fetch notifications when opening
    if (newState) {
      fetchRecentNotifications();
    }
  };

  // Mark a notification as read
  const markNotificationAsRead = async (notificationId, link) => {
    try {
      await notificationService.markAsRead(notificationId);

      // Update local state
      setNotifications(prevNotifications =>
        prevNotifications.map(notif =>
          notif.id === notificationId
            ? { ...notif, is_read: true }
            : notif
        )
      );

      // Decrease unread count
      setUnreadNotifications(prev => Math.max(0, prev - 1));

      // Navigate if there's a link
      if (link) {
        navigate(link);
      }

      // Close dropdown
      setNotificationsOpen(false);
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  // Format notification date
  const formatNotificationDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMins / 60);
    const diffDays = Math.floor(diffHours / 24);

    if (diffMins < 1) {
      return 'Just now';
    } else if (diffMins < 60) {
      return `${diffMins} min${diffMins === 1 ? '' : 's'} ago`;
    } else if (diffHours < 24) {
      return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
    } else if (diffDays < 7) {
      return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Get icon for notification type
  const getNotificationIcon = (type) => {
    switch (type) {
      case 'message':
        return <FiMessageSquare className="text-blue-500" />;
      case 'warning':
        return <FiBell className="text-orange-500" />;
      case 'info':
      default:
        return <FiBell className="text-green-500" />;
    }
  };

  // Handle cart icon click
  const handleCartClick = () => {
    navigate('/cart');
  };

  return (
    <header className="max-h-17 flex items-center text-sm">
      {isSidebar ? (
        <div className=" items-center p-1">
          <img src="/ico/TopLeft.png" alt="Golden Group" className="h-17 w-auto" />
        </div>
      ) : (
        <>
          <button onClick={toggleSidebar} className="mr-4 text-gray-600 hover:text-gray-800 lg:hidden">
            <FaBars size={20} />
          </button>
          <div className="flex-grow flex items-center justify-between">
            {user.role !== "admin" ? (
              <div className="hidden md:block w-full bg-golden-yellow text-white p-1 text-center rounded-lg mb-2 mt-1">
                <p>
                  <img
                    src="/ico/headphones.svg"
                    alt="My Properties Icon"
                    className="inline-block mr-2 h-8"
                  />
                  Contact us for assistance via WhatsApp or email to{" "}
                  <span className="text-golden-navy"> <a href="mailto:info@goldengroup.uk" ><b>info@goldengroup.uk</b></a></span> for
                  prompt support.
                </p>
              </div>
            ) : (
              <div style={{ width: '100%' }}></div>
            )}
            <div className="flex items-center ml-4 space-x-4">
              {/* Shopping Cart */}
              <div className="relative">
                <div className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                  <FiShoppingCart
                    onClick={handleCartClick}
                    className="text-gray-600 cursor-pointer"
                    size={18}
                  />
                  {cartItemCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-golden-navy text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                      {cartItemCount > 99 ? '99+' : cartItemCount}
                    </span>
                  )}
                </div>
              </div>

              {/* Messages */}
              <div className="relative">
                <FiMail
                  className="text-gray-600 cursor-pointer"
                  size={18}
                  onClick={() => navigate('/inbox')}
                />
                {unreadMessages > 0 && (
                  <span className="absolute -top-2 -right-2 bg-blue-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                    {unreadMessages > 99 ? '99+' : unreadMessages}
                  </span>
                )}
              </div>

              {/* Notifications */}
              <div className="relative" ref={notificationsRef}>
                <FiBell
                  className="text-gray-600 cursor-pointer"
                  size={18}
                  onClick={toggleNotifications}
                />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-4 h-4 flex items-center justify-center text-xs">
                    {unreadNotifications > 99 ? '99+' : unreadNotifications}
                  </span>
                )}

                {/* Notifications Dropdown */}
                {notificationsOpen && (
                  <div className="absolute right-0 mt-2 w-72 bg-white rounded-md overflow-hidden shadow-xl z-10">
                    <div className="py-2 px-3 border-b flex justify-between items-center bg-gray-50">
                      <h3 className="text-sm font-medium">Notifications</h3>
                      {unreadNotifications > 0 && (
                        <button
                          onClick={() => navigate('/inbox')}
                          className="text-xs text-blue-500 hover:text-blue-700"
                        >
                          Mark all as read
                        </button>
                      )}
                    </div>

                    {notificationsLoading ? (
                      <div className="p-4 text-center text-gray-500">Loading...</div>
                    ) : notifications.length === 0 ? (
                      <div className="p-4 text-center text-gray-500">No notifications</div>
                    ) : (
                      <div className="max-h-80 overflow-y-auto">
                        {notifications.map(notification => (
                          <div
                            key={notification.id}
                            className={`p-3 border-b cursor-pointer hover:bg-gray-50 ${!notification.is_read ? 'bg-blue-50' : ''}`}
                            onClick={() => markNotificationAsRead(notification.id, notification.link)}
                          >
                            <div className="flex items-start">
                              <div className="flex-shrink-0 pt-1">
                                {getNotificationIcon(notification.type)}
                              </div>
                              <div className="ml-3 flex-1">
                                <div className="flex justify-between">
                                  <p className={`text-sm font-medium ${!notification.is_read ? 'text-gray-900' : 'text-gray-700'}`}>
                                    {notification.title}
                                  </p>
                                  <span className="text-xs text-gray-500">
                                    {formatNotificationDate(notification.created_at)}
                                  </span>
                                </div>
                                <p className="text-xs text-gray-600 mt-1">
                                  {notification.message}
                                </p>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <div className="py-2 px-3 text-center border-t">
                      <button
                        onClick={() => {
                          setNotificationsOpen(false);
                          navigate('/inbox');
                        }}
                        className="text-sm text-blue-500 hover:text-blue-700"
                      >
                        View all notifications
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* User Profile */}
              <div className="relative" ref={dropdownRef}>
                <div className="flex items-center cursor-pointer" onClick={toggleDropdown}>
                  <div className="w-7 h-7 bg-golden-blue rounded-full mr-2"></div>
                  <span className="font-medium text-gray-700 mr-1 text-sm">
                
                     {`${user?.first_name || ''} ${user?.last_name || ''}`}
                    <br />
                    <span className="text-xs text-gray-500">{user?.email}</span>
                  </span>
                  <IoMdArrowDropdown className="text-gray-600" size={18} />
                </div>
                {dropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md overflow-hidden shadow-xl z-10">
                    <a href="/profile" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                      <FiUser className="mr-2" />
                      Profile
                    </a>
                    <a href="/inbox" className="block px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                      <FiMail className="mr-2" />
                      Inbox
                      {(unreadMessages + unreadNotifications) > 0 && (
                        <span className="ml-2 bg-red-500 text-white text-xs rounded-full px-2 py-0.5">
                          {unreadMessages + unreadNotifications}
                        </span>
                      )}
                    </a>
                    <button onClick={logout} className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-100 flex items-center">
                      <FiLogOut className="mr-2" />
                      Logout
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </>
      )}
    </header>
  );
};

export default Header;
